/**
 * src/components/teacher-dashboard-components/theme/components/Button.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../ThemeContext';
import { getColor, getTypography, createTransition } from '../themeUtils';

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.$size === 'small' ? '0.5rem 1rem' : 
                     props.$size === 'large' ? '0.75rem 2rem' : '0.625rem 1.5rem'};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  text-decoration: none;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-weight: 500;
  text-align: center;
  outline: none;
  position: relative;
  overflow: hidden;
  ${createTransition()};
  
  ${props => {
    const typography = getTypography(props.$size === 'small' ? 'small' : 'body', props.theme.isMobile);
    return `
      font-size: ${typography.fontSize};
      line-height: ${typography.lineHeight};
    `;
  }}
  
  ${props => props.$variant === 'primary' && `
    background: ${getColor('primary.blue')};
    color: ${getColor('neutral.white')};
    box-shadow: ${props.theme.shadows.sm};
    
    &:hover:not(:disabled) {
      background: ${getColor('primary.lightBlue')};
      box-shadow: ${props.theme.shadows.md};
      transform: translateY(-1px);
    }
    
    &:active {
      background: ${getColor('primary.blue')};
      transform: translateY(0);
      box-shadow: ${props.theme.shadows.sm};
    }
  `}
  
  ${props => props.$variant === 'secondary' && `
    background: ${getColor('primary.skyBlue')};
    color: ${getColor('neutral.white')};
    box-shadow: ${props.theme.shadows.sm};
    
    &:hover:not(:disabled) {
      background: ${getColor('primary.lightBlue')};
      box-shadow: ${props.theme.shadows.md};
      transform: translateY(-1px);
    }
    
    &:active {
      background: ${getColor('primary.skyBlue')};
      transform: translateY(0);
    }
  `}
  
  ${props => props.$variant === 'outline' && `
    background: transparent;
    color: ${getColor('primary.blue')};
    border: 2px solid ${getColor('primary.blue')};
    
    &:hover:not(:disabled) {
      background: ${getColor('primary.blue')};
      color: ${getColor('neutral.white')};
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
  
  ${props => props.$variant === 'ghost' && `
    background: transparent;
    color: ${getColor('primary.blue')};
    
    &:hover:not(:disabled) {
      background: rgba(17, 63, 103, 0.1);
    }
    
    &:active {
      background: rgba(17, 63, 103, 0.15);
    }
  `}
  
  ${props => props.$variant === 'success' && `
    background: ${getColor('semantic.success')};
    color: ${getColor('neutral.white')};
    
    &:hover:not(:disabled) {
      background: #0d9668;
      transform: translateY(-1px);
    }
  `}
  
  ${props => props.$variant === 'warning' && `
    background: ${getColor('semantic.warning')};
    color: ${getColor('neutral.white')};
    
    &:hover:not(:disabled) {
      background: #d97706;
      transform: translateY(-1px);
    }
  `}
  
  ${props => props.$variant === 'error' && `
    background: ${getColor('semantic.error')};
    color: ${getColor('neutral.white')};
    
    &:hover:not(:disabled) {
      background: #dc2626;
      transform: translateY(-1px);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
  
  &:focus-visible {
    outline: 2px solid ${getColor('primary.skyBlue')};
    outline-offset: 2px;
  }
  
  ${props => props.$fullWidth && `
    width: 100%;
  `}
  
  ${props => props.theme.isMobile && `
    min-height: 44px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  `}
`;

const LoadingSpinner = styled.div`
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

/**
 * Button.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  loading = false,
  disabled = false,
  fullWidth = false,
  onClick,
  type = 'button',
  className,
  ...props
}) => {
  const theme = useTheme();
  
  return (
    <StyledButton
      theme={theme}
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={onClick}
      type={type}
      className={className}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {!loading && children}
    </StyledButton>
  );
};

export default Button;
